import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Button, Form, Image } from "react-bootstrap";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { useApi } from '../api/contexts/ApiContext';
import { api_url } from '../api/config/config';
import Spinner from 'react-bootstrap/Spinner';

interface BidUploadProps {
    opportunity_id: number;
    bid_mode: string;
    confirm: boolean;
    is_end?: boolean;
    onFilesSelected?: (files: FileList | null) => void;
    standaloneUpload?: boolean;
}

const BidUpload: React.FC<BidUploadProps> = ({
    opportunity_id,
    bid_mode,
    confirm,
    is_end = false,
    onFilesSelected,
    standaloneUpload = true,
}) => {
    const { userInfo, userToken, error } = useSelector((state: RootState) => state.auth);
    const { vendorInfo } = useSelector((state: RootState) => state.vendor);
    const { getModule } = useApi();

    const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
    const [bid, setBid] = useState<any>();
    const [loading, setLoading] = useState<boolean>(false);


    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const bucketName = "enebase";

    const fetchBid = async () => {
        setLoading(true);
        const bidApi = await getModule('BidApi');
        if (userInfo) {
            const body = {
                user_id: userInfo.id,
                opportunity_id: opportunity_id,
                vendor_id: vendorInfo?.vendor_id
            }

            const response = await bidApi.hasAlreadyBid(body);
            let bid_id = response.data as number;

            if (bid_id) {
                let response = await bidApi.getBidById({ bid_id: bid_id });
                const bid: any = response.data;
                setBid(bid);
            }
        }
        setLoading(false);
    }

    useEffect(() => {
        fetchBid();
    }, []);

    const fileUpload = async (): Promise<boolean> => {
        if (selectedFiles) {
            const bidApi = await getModule('BidApi');
            try {
                const formData = new FormData();
                formData.append('bid_id', bid?.id.toString() ?? '');
                formData.append('bid_mode', bid_mode);
                formData.append('user_id', userInfo?.id.toString() ?? '');
                formData.append('vendor_id', vendorInfo?.vendor_id.toString() ?? '');
                formData.append('opportunity_id', opportunity_id.toString());
                for (let i = 0; i < selectedFiles.length; i++) {
                    formData.append('files[]', selectedFiles[i]);
                }
                let response = await fetch(`${api_url}/tenderUploadFileMultiple`, {
                    method: 'POST',
                    body: formData
                })
                const res = await response.json();
                if (!response.ok) {
                    throw new Error('file upload fails');
                }
            } catch (error) {
                console.error(`Failed to upload:`, error);
                alert('アップロードに失敗しました');
                return false;
            }
        }
        return true;

    }

    const handleFileUpload = async () => {
        setLoading(true);

        try {
            const bidApi = await getModule('BidApi');

            if (userInfo && selectedFiles) {
                for (let i = 0; i < selectedFiles.length; i++) {
                    if (selectedFiles[i].size > 52428800) { // 50MB
                        alert('ファイルサイズは50MBまでです。');
                        return;
                    }
                    if (bid?.files && bid.files.some((file: any) => file.upload_file_name === selectedFiles[i].name)) {
                        alert('ファイル名が重複しています。別の名前に変更してから、再度アップロードしてください。');
                        return;
                    }
                }

                const result = await fileUpload();
                if (result) {
                    setSelectedFiles(null);
                    if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                    }
                    alert('アップロードに成功しました');
                    await fetchBid();
                }
            };

        } catch (error) {
            console.error('Error uploading the file:', error);
            alert('アップロードに失敗しました');
        } finally {
            setLoading(false);
        }
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        setSelectedFiles(files);
        if (onFilesSelected) {
            onFilesSelected(files); // 親へ通知
        }
    };

    const fileDelete = async (filePath: string): Promise<boolean> => {
        if (bid.id) {
            const formData = new FormData();
            formData.append('bid_id', bid?.id.toString() ?? '');
            formData.append('bid_mode', bid_mode);
            formData.append('user_id', userInfo?.id.toString() ?? '');
            formData.append('vendor_id', vendorInfo?.vendor_id.toString() ?? '');
            formData.append('opportunity_id', opportunity_id.toString());
            formData.append('delete_file_path', filePath);
            let response = await fetch(`${api_url}/tenderDeleteFile`, {
                method: 'POST',
                body: formData
            })
            if (response.ok) return true;
        }
        return false;
    }

    const handleFileDelete = async (filePath: string) => {
        try {

            if (userInfo) {
                const confirm = window.confirm('ファイルを削除しますか？');
                if (!confirm) {
                    return;
                }

                const result = await fileDelete(filePath);
                if (result) {
                    // setSelectedFiles(null);
                    alert('ファイルを削除しました');
                    await fetchBid();

                } else {
                    alert('ファイルを削除に失敗しました');
                }
            };

        } catch (error) {
            console.error('Error deleting the file:', error);
            alert('ファイルを削除に失敗しました');
        }
    };

    const handleDownloadZip = async () => {
        if (bid?.files) {
            const formData = new FormData();
            formData.append('bid_id', bid.id);

            try {
                const response = await fetch(`${api_url}/downloadZip`, {
                    method: 'POST',
                    body: formData
                });

                if (response.ok) {
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = '御見積書.zip';
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                } else {
                    console.error('Failed to download ZIP');
                }
            } catch (error) {
                console.error('Error downloading ZIP:', error);
            }
        }
    };

    // if (confirm && (!bid?.files || bid.files.length === 0)) {
    //     return null;
    // }

    return (
        <div className="bg-ass-demo rounded-12 p-4 mb-5">
            <div className="row mb-4">
                <div className="col-lg-5 col-md-7 col-10 mx-auto py-3 mb-3">
                    <div className="bg-white text-center rounded-pill text-sm py-1 fw-300">
                        <Image className="height-25 mb-1 me-2" src="assets/images/other/icon-list-clip-art.svg" />見積書アップロード
                    </div>
                </div>
            </div>
            <div className="row mb-4 text-center">
                <div className="col-lg-6 col-md-8 col-10 mx-auto">
                    {loading ? (
                        <div className="d-flex justify-content-center align-items-center mb-5" style={{ height: '200px' }}>
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : (
                        <>
                            {bid?.files && bid?.files.length > 0 ? (
                                <div id="download-block" className="mb-5 bg-white rounded-12 p-lg-5 p-4 text-sm fw-400">
                                    {bid.files.map((file: any) => {
                                        return (
                                            <div className="m-lg-4 m-3" key={file.upload_file_path}>
                                                <Image className="height-50 mb-1 me-2" src="assets/images/other/icon-pdf.svg" />
                                                <a href={file.upload_file_path} target="_blank" rel="noopener noreferrer">
                                                    <span className="target_file_name">{file.upload_file_name}</span>
                                                </a>
                                                {!is_end &&
                                                    <a className="pl-1 delete-file" data-name="Company" href="javascript:void(0);" onClick={() => handleFileDelete(file.upload_file_path)}>
                                                        <Image className="height-25" src="assets/images/other/icon-cross-white-bg-ass-circle.svg" />
                                                    </a>
                                                }
                                            </div>
                                        )
                                    })}

                                    <Button
                                        id="download-zip"
                                        type="button"
                                        className="btn-lg btn-theme btn-primary rounded-pill px-lg-5 px-3 me-2"
                                        onClick={handleDownloadZip}
                                    >
                                        <span className="mx-3">ダウンロード</span>
                                    </Button>
                                </div>
                            ) : null}

                            {!is_end && (
                                <div id="upload-block">
                                    <div className="border-dashed rounded-12 p-lg-5 p-4 mb-lg-5 mb-4 text-sm text-ass-07 fw-400">
                                        <form id="upload-form" method="post" encType="multipart/form-data">
                                            <input id="bid_mode" type="hidden" name="bid_mode" value={bid_mode} />
                                            <input type="hidden" name="opportunity_id" value={opportunity_id} />
                                            <input type="file" name="file[]" multiple accept="application/pdf,application/zip"
                                                onChange={handleFileChange} ref={fileInputRef} />
                                        </form>
                                    </div>

                                    {selectedFiles && selectedFiles.length > 0 && standaloneUpload ? (
                                        <Button
                                            id="upload"
                                            type="button"
                                            className="btn-lg btn-theme btn-primary rounded-pill px-lg-5 px-3 me-2"
                                            onClick={handleFileUpload}
                                        >
                                            <span className="mx-3">アップロード</span>
                                        </Button>
                                    ) : null}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

export default BidUpload;
